import React, { useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Close from '@material-ui/icons/Close';
import { Text, Button, Link, Box } from '@solvhealth/jigsaw-v1';
import { smallerThanOrEqualTo, biggerThanOrEqualTo } from '~/core/util/styledComponents';
import { colors } from '../../../constants/colors';
import { fontFamily, fontSize } from '../../../constants/text';
import { SolvReduxState } from '../../../reducers';
import { REQUEST_UNIVERSAL_SEARCH } from '../../../sagas/universalSearch';
import AutocompleteResults from '../AutocompleteResults';
import Header from '../Header';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';
import { analyticsTrackEvent } from '~/core/analytics';
import {
  ACTION_CTA_CLICK,
  ACTION_LOAD,
  SKIP_TO_SEARCH,
  UNIVERSAL_SEARCH_MODAL,
} from '~/core/analytics/events';
import { isAStructuredRfv } from '../util';

const Root = styled.div.withConfig({ componentId: 'sc-1wh5m9y-0' })`
  display: flex;
  flex-direction: column;
  padding: 40px 40px 48px 40px;
  background-color: ${
    // @ts-expect-error ts-migrate(2551) FIXME: Property 'greyDim' does not exist on type '{ fadeT... Remove this comment to see the full error message
    colors.greyDim
  };

  min-height: 100%;
  ${smallerThanOrEqualTo.sm`
    padding: 40px 20px 48px 20px;
  `}
`;

const ContentContainer = styled.div.withConfig({ componentId: 'sc-1wh5m9y-1' })`
  padding: 24px 16px;
  text-align: center;
`;

const ErrorReason = styled.div.withConfig({ componentId: 'sc-1wh5m9y-2' })`
  margin: 0 auto;
  font-size: ${fontSize.default};
  font-family: ${fontFamily.book};
  line-height: 24px;
  color: ${colors.squid};

  & em {
    color: ${colors.crunchBerry};
    font-style: normal;
  }

  & b {
    font-family: ${fontFamily.bold};
    font-weight: normal;
  }
`;

const StyledText = styled(Text).withConfig({ componentId: 'sc-1wh5m9y-3' })`
  margin: 0 auto 0 auto;
  max-width: 432px;
  padding: 0 32px 0 32px;
  text-align: center;

  ${smallerThanOrEqualTo.sm`
    padding: 0px 0px 12px 0px;
    text-align: start;
  `}
`;

const StyledLink = styled(Link).withConfig({ componentId: 'sc-1wh5m9y-4' })`
  cursor: pointer;
  margin-right: 24px;
`;

const StyledLinkMobile = styled(Link).withConfig({ componentId: 'sc-1wh5m9y-5' })`
  cursor: pointer;
  margin: 8px auto 0px 0px;
  ${biggerThanOrEqualTo.sm`
    display: none;  
  `}
`;
const StyledBox = styled(Box).withConfig({ componentId: 'sc-1wh5m9y-6' })`
  display: flex;
  justify-content: flex-end;
  ${smallerThanOrEqualTo.sm`
    display: none;
  `}
`;

type Props = {
  onClickBack?: () => void;
  requestSearch?: (doc: any) => void;
  fromSrp?: boolean;
  results?: any[];
};

const UserChoiceContent = ({ fromSrp = false, onClickBack, results = [] }: Props) => {
  const { universalSearch, position, searchPreferences } = useSelector((state: SolvReduxState) => ({
    universalSearch: state.universalSearch,
    position: state.position,
    searchPreferences: state.searchPreferences,
  }));

  const dispatch = useDispatch();
  const requestSearch = useCallback(
    (fullDoc: any) =>
      dispatch({
        type: REQUEST_UNIVERSAL_SEARCH,
        fullDoc,
        fromSrp,
      }),
    [dispatch, fromSrp]
  );

  const suggestionCopy = useMemo(
    () =>
      universalSearch?.results?.length
        ? 'Try the suggestions below'
        : 'Try the options below or refine your search',
    [universalSearch.results.length]
  );

  const locationLabel = useMemo(
    () => searchPreferences?.location?.label ?? position?.result?.label ?? '',
    [position, searchPreferences]
  );

  const remappedSuggestions = results.map((result: any) => ({
    ...result,
    onClick: () => {
      requestSearch(result.fullDoc);
    },
  }));

  const handleSkip = () => {
    analyticsTrackEvent(SKIP_TO_SEARCH, {
      action: ACTION_CTA_CLICK,
      value: 'Skip and show results',
      query: universalSearch?.query,
      isQueryAStructuredRfv: isAStructuredRfv(universalSearch.query, universalSearch.results),
    });
    requestSearch({ type: 'symptom', highlight: '', name: 'Feeling sick' });
  };

  useEffect(() => {
    analyticsTrackEvent(UNIVERSAL_SEARCH_MODAL, {
      action: ACTION_LOAD,
      query: universalSearch?.query,
      isQueryAStructuredRfv: isAStructuredRfv(universalSearch.query, universalSearch.results),
    });
  }, [universalSearch?.query, universalSearch?.results]);

  return (
    <Root>
      <Close
        onClick={onClickBack}
        style={{
          marginLeft: 'auto',
          marginBottom: '16px',
          cursor: 'pointer',
          color: colors.gray400,
        }}
      />
      <StyledText fontSize="lg" fontWeight="bold">
        Are you searching for one of these suggestions?
      </StyledText>

      <AutocompleteResults results={remappedSuggestions} />

      <StyledBox>
        <StyledLink onClick={onClickBack}>Back to search</StyledLink>
        <Button onClick={handleSkip}>Skip and show results</Button>
      </StyledBox>
      {/* MOBILE */}
      <StyledLinkMobile onClick={handleSkip}>Skip and show results</StyledLinkMobile>
    </Root>
  );
};

export default UserChoiceContent;
